import { useState } from 'react'
import { Bell, CreditCard, Lock, Minus, Plus, User } from 'lucide-react'
import { useTopBar } from '@/contexts/TopBarContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { useAuth } from '@/contexts/AuthContext'
import { adminApi, type ChangePreview } from '@/api/admin'

const statusConfig: Record<string, { label: string; classes: string }> = {
  Trialing:   { label: 'Trial',    classes: 'bg-indigo-100 text-indigo-700' },
  Active:     { label: 'Active',   classes: 'bg-green-100 text-green-700' },
  PastDue:    { label: 'Past due', classes: 'bg-amber-100 text-amber-700' },
  Canceled:   { label: 'Canceled', classes: 'bg-red-100 text-red-700' },
  Incomplete: { label: 'Incomplete', classes: 'bg-slate-100 text-slate-600' },
}

// £/month (matches the signup page); a year up front is 10 months for the price of 12.
const PLAN_PRICE = { Starter: 49, Pro: 99 } as const
const SEAT_PRICE = 25
const ANNUAL_MONTHS = 10
const ANNUAL_SEAT_PRICE = SEAT_PRICE * ANNUAL_MONTHS

// The exact amount Stripe says a change will cost, shown before the customer confirms it.
function ChangeAmount({ preview, loading, failed }: { preview?: ChangePreview; loading: boolean; failed: boolean }) {
  if (loading) return <p className="text-sm text-slate-500">Working out the exact amount…</p>
  if (failed || !preview) return <p className="text-xs text-amber-700">We couldn't work out the exact amount just now. Please try again in a moment.</p>
  if (preview.isTrial) return null // the trial wording already says nothing is charged
  const money = `£${(Math.abs(preview.amountPence) / 100).toFixed(2)}`
  if (preview.amountPence === 0) return <p className="text-sm font-semibold text-slate-900">There's no charge for this change.</p>
  if (preview.amountPence < 0) return <p className="text-sm font-semibold text-slate-900">A credit of {money} will be applied to your next invoice.</p>
  return preview.chargedNow
    ? <p className="text-sm font-semibold text-slate-900">{money} will be charged to your payment method now.</p>
    : <p className="text-sm font-semibold text-slate-900">{money} (pro-rata for the rest of this billing period) will be added to your next invoice.</p>
}

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

function Toggle({ label, description, checked, onChange }: {
  label: string
  description: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0 border-b border-slate-100 last:border-0">
      <div>
        <p className="text-sm font-medium text-slate-800">{label}</p>
        <p className="text-xs text-slate-500 mt-0.5">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${
          checked ? 'bg-indigo-600' : 'bg-slate-200'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  )
}

export default function AccountSettingsPage() {
  useTopBar(<span className="text-sm font-medium text-gray-900">Account settings</span>)
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const initialTab = ['account', 'notifications', 'billing'].includes(searchParams.get('tab') ?? '')
    ? searchParams.get('tab')!
    : 'account'

  // ── Password ──────────────────────────────────────────────────────────────
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      adminApi.changePassword(data),
    onSuccess: () => {
      setPasswordSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setPasswordError('')
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail ?? err?.response?.data?.title
      setPasswordError(detail ?? 'Failed to update password. Check your current password and try again.')
      setPasswordSuccess(false)
    },
  })

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordError('')
    setPasswordSuccess(false)
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.')
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.')
      return
    }
    changePasswordMutation.mutate({ currentPassword, newPassword })
  }

  // ── Notification preferences ──────────────────────────────────────────────
  const { data: savedPrefs } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => adminApi.getNotificationPreferences().then(r => r.data),
  })

  const [notifications, setNotifications] = useState({
    expiryReminders: true,
    bookingConfirmations: true,
    weeklyDigest: false,
    invoiceAlerts: true,
  })

  const [prefsSynced, setPrefsSynced] = useState(false)
  if (savedPrefs && !prefsSynced) {
    setNotifications(savedPrefs)
    setPrefsSynced(true)
  }

  const [prefsSaving, setPrefsSaving] = useState(false)
  const [prefsSuccess, setPrefsSuccess] = useState(false)

  const savePrefs = async () => {
    setPrefsSaving(true)
    setPrefsSuccess(false)
    try {
      await adminApi.updateNotificationPreferences(notifications)
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] })
      setPrefsSuccess(true)
      setTimeout(() => setPrefsSuccess(false), 3000)
    } finally {
      setPrefsSaving(false)
    }
  }

  // ── Billing ───────────────────────────────────────────────────────────────
  const { data: billingData, isLoading: billingLoading } = useQuery({
    queryKey: ['billing-info'],
    queryFn: () => adminApi.getBillingInfo(),
  })

  const { data: seats } = useQuery({
    queryKey: ['trainer-seats'],
    queryFn: () => adminApi.getTrainerSeatInfo().then(r => r.data),
  })

  const [pendingAddon, setPendingAddon] = useState<number | null>(null)
  const [confirmingSeats, setConfirmingSeats] = useState(false)
  const addonSeats = pendingAddon ?? seats?.addon ?? 0

  const { data: seatPreview, isLoading: seatPreviewLoading, isError: seatPreviewError } = useQuery({
    queryKey: ['seat-preview', pendingAddon],
    queryFn: () => adminApi.getSeatChangePreview(pendingAddon ?? 0),
    enabled: confirmingSeats && pendingAddon !== null,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })

  const seatsMutation = useMutation({
    mutationFn: ({ seats: n, prorationDate }: { seats: number; prorationDate?: string }) => adminApi.updateTrainerSeats(n, prorationDate),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['trainer-seats'] })
      queryClient.invalidateQueries({ queryKey: ['billing-info'] })
      setPendingAddon(null)
      setConfirmingSeats(false)
      // Held until paid: the notice at the top of this tab has the link to finish paying.
      if (result.pending) window.scrollTo({ top: 0, behavior: 'smooth' })
    },
  })

  const portalMutation = useMutation({
    mutationFn: () => adminApi.createBillingPortalSession(window.location.origin + '/admin/account'),
    onSuccess: (url) => { window.open(url, '_blank', 'noopener') },
  })

  const reactivateMutation = useMutation({
    mutationFn: () => adminApi.reactivateSubscription(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing-info'] }),
  })

  const cancelPendingMutation = useMutation({
    mutationFn: () => adminApi.cancelPendingChange(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-info'] })
      queryClient.invalidateQueries({ queryKey: ['trainer-seats'] })
    },
  })

  const [showSwitch, setShowSwitch] = useState(false)
  const { data: switchPreview, isLoading: switchPreviewLoading, isError: switchPreviewError } = useQuery({
    queryKey: ['annual-switch-preview'],
    queryFn: () => adminApi.getAnnualSwitchPreview(),
    enabled: showSwitch,
    staleTime: 0,
    gcTime: 0,
  })
  const switchMutation = useMutation({
    mutationFn: () => adminApi.switchToAnnual(),
    onSuccess: () => {
      setShowSwitch(false)
      queryClient.invalidateQueries({ queryKey: ['billing-info'] })
      queryClient.invalidateQueries({ queryKey: ['trainer-seats'] })
    },
  })

  const [confirmingPlan, setConfirmingPlan] = useState<'Starter' | 'Pro' | null>(null)
  const { data: planPreview, isLoading: planPreviewLoading, isError: planPreviewError } = useQuery({
    queryKey: ['plan-preview', confirmingPlan],
    queryFn: () => adminApi.getPlanChangePreview(confirmingPlan!),
    enabled: confirmingPlan !== null,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })
  const changePlanMutation = useMutation({
    mutationFn: ({ plan, prorationDate }: { plan: 'Starter' | 'Pro'; prorationDate?: string }) => adminApi.changePlan(plan, prorationDate),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['billing-info'] })
      queryClient.invalidateQueries({ queryKey: ['company-profile'] })
      setConfirmingPlan(null)
      if (result.pending) window.scrollTo({ top: 0, behavior: 'smooth' })
    },
  })

  const billingStatus = billingData
    ? (statusConfig[billingData.subscriptionStatus] ?? { label: billingData.subscriptionStatus, classes: 'bg-slate-100 text-slate-600' })
    : null

  const currentPlan = (billingData?.planTier ?? 'Starter') as 'Starter' | 'Pro'
  const isAnnual = billingData?.billingInterval === 'Annual'
  const isTrialing = billingData?.subscriptionStatus === 'Trialing'
  // One change at a time: while Stripe is holding one for payment, further seat/plan changes wait.
  const changeLocked = billingData?.hasPendingChange === true
  // A yearly change is charged to the card on file the moment it's confirmed (outside a trial).
  const paysNow = isAnnual && !isTrialing
  const seatPrice = isAnnual ? ANNUAL_SEAT_PRICE : SEAT_PRICE

  return (
    <div>
      <Tabs defaultValue={initialTab} className="max-w-lg">
        <div className="flex overflow-x-auto mb-6">
          <TabsList className="shrink-0">
            <TabsTrigger value="account">
              <User size={14} className="mr-1.5" /> Account
            </TabsTrigger>
            <TabsTrigger value="notifications">
              <Bell size={14} className="mr-1.5" /> Notifications
            </TabsTrigger>
            <TabsTrigger value="billing">
              <CreditCard size={14} className="mr-1.5" /> Billing
            </TabsTrigger>
          </TabsList>
          <span className="w-4 shrink-0 sm:hidden" aria-hidden="true" />
        </div>

        {/* Account tab — email + password */}
        <TabsContent value="account" className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <User size={14} className="text-slate-400" /> Profile
              </h2>
              <div className="space-y-1.5">
                <Label>Email address</Label>
                <Input value={user?.email ?? ''} disabled className="bg-slate-50 text-slate-500" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Lock size={14} className="text-slate-400" /> Password
            </h2>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="current-password">Current password</Label>
                <Input
                  id="current-password"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              {passwordError && <p className="text-sm text-red-600">{passwordError}</p>}
              {passwordSuccess && <p className="text-sm text-green-600">Password updated successfully.</p>}
              <Button type="submit" disabled={changePasswordMutation.isPending}>
                {changePasswordMutation.isPending ? 'Updating…' : 'Update password'}
              </Button>
            </form>
          </div>
        </TabsContent>

        {/* Notifications tab */}
        <TabsContent value="notifications">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <Toggle
              label="Certificate expiry reminders"
              description="Send reminders to staff members before their certifications expire."
              checked={notifications.expiryReminders}
              onChange={v => setNotifications(n => ({ ...n, expiryReminders: v }))}
            />
            <Toggle
              label="Booking confirmations"
              description="Send email confirmation to the client when a booking is made."
              checked={notifications.bookingConfirmations}
              onChange={v => setNotifications(n => ({ ...n, bookingConfirmations: v }))}
            />
            <Toggle
              label="Weekly digest"
              description="A weekly summary of upcoming bookings, expiring certs, and open enquiries."
              checked={notifications.weeklyDigest}
              onChange={v => setNotifications(n => ({ ...n, weeklyDigest: v }))}
            />
            <Toggle
              label="Invoice alerts"
              description="Notify you when an invoice becomes overdue."
              checked={notifications.invoiceAlerts}
              onChange={v => setNotifications(n => ({ ...n, invoiceAlerts: v }))}
            />
            <div className="pt-4 flex items-center gap-3">
              <Button onClick={savePrefs} disabled={prefsSaving}>
                {prefsSaving ? 'Saving…' : 'Save preferences'}
              </Button>
              {prefsSuccess && <p className="text-sm text-green-600">Saved.</p>}
            </div>
          </div>
        </TabsContent>

        {/* Billing tab */}
        <TabsContent value="billing">
          {billingLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : billingData ? (
            <div className="space-y-6">
              {billingData.hasPendingChange && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 space-y-2">
                  <p className="font-semibold">A change to your subscription is waiting for payment</p>
                  <p>
                    Your bank may need to approve the payment, or you can use a different card. Nothing has been changed or charged yet,
                    and it will be applied as soon as the payment goes through. When you've paid, come back to this tab and it will update.
                    If you do nothing it expires by itself after about 23 hours.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    {billingData.pendingPaymentUrl && (
                      <a
                        href={billingData.pendingPaymentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-8 items-center rounded-md bg-amber-600 px-3 text-xs font-medium text-white hover:bg-amber-700"
                      >
                        Complete payment
                      </a>
                    )}
                    <Button size="sm" variant="outline" className="h-8 text-xs" disabled={cancelPendingMutation.isPending} onClick={() => cancelPendingMutation.mutate()}>
                      {cancelPendingMutation.isPending ? 'Cancelling…' : 'Cancel this change'}
                    </Button>
                  </div>
                  {cancelPendingMutation.isError && <p className="text-red-700">Sorry, we couldn't cancel it. Please try again.</p>}
                </div>
              )}

              {!billingData.isActive && (billingData.subscriptionStatus === 'Canceled' || billingData.subscriptionStatus === 'Incomplete') && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  Your subscription is {billingData.subscriptionStatus.toLowerCase()} — some features may be restricted.
                </div>
              )}

              {billingData.cancelAtPeriodEnd && billingData.isActive && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  Your subscription is scheduled to cancel
                  {billingData.currentPeriodEndsAt ? ` on ${fmtDate(billingData.currentPeriodEndsAt)}` : ' at the end of the current period'}.
                  You'll keep access until then.
                  <div className="mt-2">
                    <Button size="sm" onClick={() => reactivateMutation.mutate()} disabled={reactivateMutation.isPending}>
                      {reactivateMutation.isPending ? 'Updating…' : 'Keep my subscription'}
                    </Button>
                    {reactivateMutation.isError && <span className="ml-3 text-red-600">Couldn't update it. Please try again.</span>}
                  </div>
                </div>
              )}

              {/* Subscription status row */}
              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                {billingStatus && (
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${billingStatus.classes}`}>
                    {billingStatus.label}
                  </span>
                )}
                {isAnnual && <span>Billed yearly</span>}
                {billingData.subscriptionStatus === 'Trialing' && billingData.trialEndsAt && (
                  <span>Trial ends {fmtDate(billingData.trialEndsAt)}</span>
                )}
                {billingData.subscriptionStatus === 'Active' && billingData.currentPeriodEndsAt && (
                  <span>
                    {billingData.cancelAtPeriodEnd
                      ? `Cancels on ${fmtDate(billingData.currentPeriodEndsAt)}`
                      : `${isAnnual ? 'Renews on' : 'Next billing date'}: ${fmtDate(billingData.currentPeriodEndsAt)}`}
                  </span>
                )}
              </div>

              {/* Plan comparison cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(['Starter', 'Pro'] as const).map(plan => {
                  const isCurrent = currentPlan === plan
                  const isConfirming = confirmingPlan === plan
                  const features = plan === 'Starter'
                    ? ['1 certificate design', 'Unlimited bookings & clients', 'Basic email notifications', 'Standard reporting']
                    : ['10 certificate designs', 'Verification QR codes', 'Custom notification schedules', 'Advanced analytics dashboard']
                  return (
                    <div key={plan} className={`rounded-xl border-2 p-5 transition-colors ${isCurrent ? 'border-indigo-500 bg-indigo-50/40' : 'border-slate-200 bg-white'}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-slate-900">{plan}</p>
                          {plan === 'Pro' && <p className="text-xs text-indigo-600 font-medium mt-0.5">Recommended</p>}
                        </div>
                        {isCurrent && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-700 rounded-full px-2 py-0.5">
                            Current plan
                          </span>
                        )}
                      </div>
                      <ul className="space-y-1.5 mb-5">
                        {features.map(f => (
                          <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                            <span className="mt-1 shrink-0 w-3.5 h-3.5 rounded-full bg-indigo-100 flex items-center justify-center">
                              <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                            </span>
                            {f}
                          </li>
                        ))}
                      </ul>
                      {!isCurrent && (
                        isAnnual && plan === 'Starter' ? (
                          <p className="text-xs text-slate-500">
                            On a yearly plan, moving to a lower plan happens at renewal.{' '}
                            <Link to="/admin/support" className="text-indigo-600 hover:underline">Contact support</Link> to arrange it.
                          </p>
                        ) : isConfirming ? (
                          <div className="space-y-2">
                            <ChangeAmount preview={planPreview} loading={planPreviewLoading} failed={planPreviewError} />
                            <p className="text-xs text-slate-600">
                              {plan === 'Pro'
                                ? (isTrialing
                                    ? "You're on your free trial, so nothing is charged now. Pro will be included when your trial ends."
                                    : isAnnual
                                      ? 'This is the pro-rata difference for the rest of your year. Pro then renews with your plan.'
                                      : 'Pro then renews monthly with your plan.')
                                : 'Switching to Starter will take effect immediately. Pro features will be removed.'}
                            </p>
                            {changePlanMutation.isError && (
                              <p className="text-xs text-red-600">{(changePlanMutation.error as any)?.response?.data?.detail ?? 'Failed to switch plan. Please try again.'}</p>
                            )}
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                // On a yearly plan the card is charged, so never confirm without showing the exact amount.
                                disabled={changePlanMutation.isPending || planPreviewLoading || (paysNow && !planPreview)}
                                onClick={() => changePlanMutation.mutate({ plan, prorationDate: planPreview?.prorationDate })}
                              >
                                {changePlanMutation.isPending
                                  ? 'Switching…'
                                  : paysNow && plan === 'Pro' && planPreview && planPreview.amountPence > 0
                                    ? `Confirm and pay £${(planPreview.amountPence / 100).toFixed(2)}`
                                    : 'Confirm switch'}
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setConfirmingPlan(null)}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant={plan === 'Pro' ? 'default' : 'outline'}
                            disabled={changeLocked}
                            onClick={() => setConfirmingPlan(plan)}
                          >
                            {plan === 'Pro' ? 'Upgrade to Pro' : 'Switch to Starter'}
                          </Button>
                        )
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Trainer seats */}
              {seats && (
                <div className="bg-white rounded-xl border border-slate-200 px-4 py-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-800">Trainer seats</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {seats.used} of {seats.base + (seats.complimentary ?? 0) + addonSeats} used · {seats.base} included in {currentPlan} plan
                        {(seats.complimentary ?? 0) > 0 && ` · ${seats.complimentary} complimentary`}
                      </p>
                    </div>
                    <span className={`text-sm font-semibold ${seats.used >= seats.base + (seats.complimentary ?? 0) + addonSeats ? 'text-amber-600' : 'text-slate-900'}`}>
                      {seats.base + (seats.complimentary ?? 0) + addonSeats} total
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-500 w-24 shrink-0">Add-on seats</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => { setPendingAddon(Math.max(0, addonSeats - 1)); setConfirmingSeats(false) }}
                        // On a yearly plan seats can only be added mid-term (removing them would leave a credit).
                        disabled={changeLocked || addonSeats === 0 || seatsMutation.isPending || (isAnnual && addonSeats <= (seats.addon ?? 0))}
                        className="h-7 w-7 rounded border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="w-6 text-center text-sm font-medium text-slate-800">{addonSeats}</span>
                      <button
                        type="button"
                        onClick={() => { setPendingAddon(addonSeats + 1); setConfirmingSeats(false) }}
                        disabled={changeLocked || seatsMutation.isPending}
                        className="h-7 w-7 rounded border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                    {pendingAddon !== null && pendingAddon !== seats.addon && !confirmingSeats && (
                      <div className="flex items-center gap-2">
                        <Button size="sm" className="h-7 text-xs" onClick={() => setConfirmingSeats(true)}>
                          Update seats
                        </Button>
                        <button type="button" onClick={() => setPendingAddon(null)} className="text-xs text-slate-400 hover:text-slate-600">
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                  {confirmingSeats && pendingAddon !== null && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 space-y-3">
                      {(() => {
                        const diff = pendingAddon - (seats.addon ?? 0)
                        const adding = diff > 0
                        return (
                          <>
                            <div>
                              <p className="text-sm font-semibold text-slate-800">
                                {adding ? `Add ${diff} seat${diff !== 1 ? 's' : ''} — £${diff * seatPrice}/${isAnnual ? 'year' : 'month'}` : `Remove ${Math.abs(diff)} seat${Math.abs(diff) !== 1 ? 's' : ''}`}
                              </p>
                            </div>
                            <ChangeAmount preview={seatPreview} loading={seatPreviewLoading} failed={seatPreviewError} />
                            <p className="text-xs text-slate-500">
                              {adding
                                ? (isTrialing
                                    ? "You're on your free trial, so nothing is charged now. These seats will be included in your first payment when the trial ends."
                                    : isAnnual
                                      ? 'This is the pro-rata share for the rest of your year. These seats then renew with your plan.'
                                      : 'These seats then renew monthly with your plan.')
                                : 'Removing seats takes effect straight away.'}
                            </p>
                            {seatsMutation.isError && <p className="text-xs text-red-600">{(seatsMutation.error as any)?.response?.data?.detail ?? 'Failed to update seats. Please try again.'}</p>}
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="h-7 text-xs"
                                // On a yearly plan the card is charged, so never confirm without showing the exact amount.
                                disabled={seatsMutation.isPending || seatPreviewLoading || (paysNow && adding && !seatPreview)}
                                onClick={() => seatsMutation.mutate({ seats: pendingAddon, prorationDate: seatPreview?.prorationDate })}
                              >
                                {seatsMutation.isPending
                                  ? 'Updating…'
                                  : paysNow && adding && seatPreview && seatPreview.amountPence > 0
                                    ? `Confirm and pay £${(seatPreview.amountPence / 100).toFixed(2)}`
                                    : 'Confirm'}
                              </Button>
                              <button type="button" onClick={() => setConfirmingSeats(false)} className="text-xs text-slate-400 hover:text-slate-600" disabled={seatsMutation.isPending}>
                                Cancel
                              </button>
                            </div>
                          </>
                        )
                      })()}
                    </div>
                  )}
                  {!confirmingSeats && (
                    <p className="text-xs text-slate-400">
                      {isAnnual
                        ? `Add-on seats are £${seatPrice}/seat/year, billed pro-rata for the rest of your year and effective immediately. Seats can be removed at renewal - contact support.`
                        : `Add-on seats are £${seatPrice}/seat/month, billed pro-rata and effective immediately.`}
                    </p>
                  )}
                </div>
              )}

              {/* Switch from monthly to yearly (10 months for the price of 12) */}
              {billingData.canSwitchToAnnual && (() => {
                const yearly = PLAN_PRICE[currentPlan] * ANNUAL_MONTHS + (seats?.addon ?? 0) * ANNUAL_SEAT_PRICE
                return (
                  <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 px-4 py-4 space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Switch to yearly billing</p>
                      <p className="text-xs text-slate-600 mt-1">Pay for 10 months and get 12. That's £{yearly} a year for your current plan{(seats?.addon ?? 0) > 0 ? ' and trainer seats' : ''}, instead of £{(yearly / ANNUAL_MONTHS).toFixed(0)} a month.</p>
                    </div>
                    {!showSwitch ? (
                      <Button size="sm" variant="outline" onClick={() => setShowSwitch(true)}>See what it costs</Button>
                    ) : switchPreviewLoading ? (
                      <p className="text-xs text-slate-500">Working it out…</p>
                    ) : switchPreviewError || !switchPreview ? (
                      <p className="text-xs text-red-600">Sorry, we couldn't work that out. Please try again.</p>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-slate-800">
                          {switchPreview.isTrial
                            ? `You're on your free trial, so nothing is charged now. When it ends you'll pay £${yearly} for the year.`
                            : `You'll be charged £${(switchPreview.amountDueNowPence / 100).toFixed(2)} now (the year, less the unused part of this month), then £${yearly} every year.`}
                        </p>
                        <p className="text-xs text-slate-500">Yearly plans can't be refunded part-way through, and moving back to monthly happens at renewal.</p>
                        {switchMutation.isError && <p className="text-xs text-red-600">{(switchMutation.error as any)?.response?.data?.detail ?? 'The switch failed, so nothing has changed. Please check your card and try again.'}</p>}
                        <div className="flex gap-2">
                          <Button size="sm" disabled={switchMutation.isPending} onClick={() => switchMutation.mutate()}>{switchMutation.isPending ? 'Switching…' : 'Switch to yearly'}</Button>
                          <Button size="sm" variant="ghost" disabled={switchMutation.isPending} onClick={() => setShowSwitch(false)}>Cancel</Button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}
              {isAnnual && (
                <p className="text-xs text-slate-500">
                  You're on yearly billing. To move to monthly at renewal, <Link to="/admin/support" className="text-indigo-600 hover:underline">contact support</Link>.
                </p>
              )}

              {/* Stripe portal — payment method & invoices */}
              <div>
                <Button variant="outline" onClick={() => portalMutation.mutate()} disabled={portalMutation.isPending}>
                  {portalMutation.isPending ? 'Redirecting…' : 'Payment method & invoices'}
                </Button>
                <p className="mt-2 text-xs text-slate-400">
                  Opens the Stripe portal to update your payment method or download invoices.
                </p>
                {portalMutation.isError && <p className="mt-2 text-sm text-red-600">Failed to open billing portal. Please try again.</p>}
              </div>

              {billingData.canCancel && (
                <div className="border-t border-slate-100 pt-4">
                  <Link to="/admin/account/cancel" className="text-sm text-slate-500 hover:text-red-600 underline underline-offset-2">
                    Cancel subscription
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-500 py-4">Unable to load billing information.</p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
